/**
 * CatastroGeocoderV2 — Geocodificador L5 por parcelas catastrales (V2)
 *
 * Envoltorio fino sobre CatastroDataService (R2 + Flatbush + uFuzzy + refcatMap).
 * Delega toda la búsqueda al DataService, que preconstruye índices al cargar
 * cada municipio, y convierte los resultados a GeocodingResult estándar.
 *
 * Estrategias de búsqueda (orden prioridad):
 *   1. Referencia catastral (exacta / prefijo 14 chars)
 *   2. Polígono + Parcela (explícito o auto-detectado)
 *   3. Dirección (vía + número)
 *   4. Fuzzy matching por nombre
 *   5. Proximidad espacial (Flatbush)
 *
 * Nivel cascada: L5 (último recurso antes de manual)
 * Precisión: ±10-25 m (centroide parcela)
 * CRS: EPSG:25830 (UTM30 ETRS89)
 * Datos: 2,4 M edificaciones — Dirección General del Catastro
 *
 * @module services/geocoding/specialized
 * @version 2.0.0
 * @date 2026-02-09
 */

import {
  getCatastroDataService,
  resetCatastroDataService,
} from '../../data/CatastroDataService';
import type {
  CatastroDataService,
  CatastroServiceStats,
  CadastralPatternMatch,
} from '../../data/CatastroDataService';
import type {
  CatastroR2SearchOptions,
  CatastroR2Result,
} from '../../../types/catastroR2';
import { tipologiaACodigoCatastro } from '../../../types/catastroR2';
import { TipologiaPTEL, TIPOLOGIAS_VALUES } from '../../../types/tipologia';
import type { GeocodingResult } from '../../../types/infrastructure';

// F1-R2-FIRST chat 1/2 V2: deps opcionales para fallback OVC online
import { CatastroService } from '../generic/CatastroService';
import { CircuitBreakerManager } from '../orchestrator/CircuitBreakerManager';

// =============================================================================
// CONFIGURACIÓN
// =============================================================================

/** Scoring según estrategia de búsqueda */
const _SCORE_CONFIG = {
  refcat: 95,
  polygonParcel: 90,
  addressWithNumero: 85,
  addressOnly: 70,
  fuzzyHigh: 80,
  fuzzyMedium: 70,
  fuzzyLow: 55,
  proximity: 75,
};

// =============================================================================
// SINGLETON
// =============================================================================

let instance: CatastroGeocoderV2 | null = null;

/**
 * Dependencias opcionales para activar el fallback OVC online en
 * `geocodeWithOVCFallback`. Si no se inyectan, el método sólo intenta R2 offline.
 *
 * D1 = (a) (F1-R2-FIRST chat 1/2 V2): la inyección es idempotente — si el
 * singleton existe sin deps y se llama con deps, se inyectan; si se llama sin
 * args, se devuelve la instancia tal cual (preserva los 5 mocks de tests
 * existentes que ignoran el argumento).
 */
export interface CatastroGeocoderV2Deps {
  ovcService?: CatastroService;
  circuitBreaker?: CircuitBreakerManager;
}

export function getCatastroGeocoderV2(deps?: CatastroGeocoderV2Deps): CatastroGeocoderV2 {
  if (!instance) {
    instance = new CatastroGeocoderV2(undefined, deps?.ovcService, deps?.circuitBreaker);
  } else if (deps) {
    if (deps.ovcService && !instance.hasOvcService()) {
      instance.injectOvcService(deps.ovcService);
    }
    if (deps.circuitBreaker && !instance.hasCircuitBreaker()) {
      instance.injectCircuitBreaker(deps.circuitBreaker);
    }
  }
  return instance;
}

export function resetCatastroGeocoderV2(): void {
  instance = null;
  resetCatastroDataService();
}

// =============================================================================
// CLASE PRINCIPAL
// =============================================================================

export class CatastroGeocoderV2 {
  private readonly service: CatastroDataService;
  // F1-R2-FIRST chat 1/2 V2: deps opcionales para fallback OVC online.
  // Mutables — pueden inyectarse perezosamente vía `getCatastroGeocoderV2(deps)`
  // si el singleton ya existe (idempotente, sólo si aún no se han fijado).
  private ovcService?: CatastroService;
  private circuitBreaker?: CircuitBreakerManager;

  constructor(
    service?: CatastroDataService,
    ovcService?: CatastroService,
    circuitBreaker?: CircuitBreakerManager,
  ) {
    this.service = service ?? getCatastroDataService();
    this.ovcService = ovcService;
    this.circuitBreaker = circuitBreaker;
  }

  // ---------------------------------------------------------------------------
  // INYECCIÓN PEREZOSA DE DEPS (F1-R2-FIRST chat 1/2 V2)
  // ---------------------------------------------------------------------------
  // Setters package-level (no diseñados para uso externo). Sólo el helper
  // `getCatastroGeocoderV2(deps?)` los invoca cuando el singleton ya existe
  // sin deps y aparece una llamada con deps. Idempotente: una vez fijada una
  // dep, no se sobreescribe (los `has*()` lo garantizan).

  /** @internal — usado por `getCatastroGeocoderV2(deps?)` para inyección perezosa. */
  hasOvcService(): boolean {
    return this.ovcService !== undefined;
  }

  /** @internal — usado por `getCatastroGeocoderV2(deps?)` para inyección perezosa. */
  hasCircuitBreaker(): boolean {
    return this.circuitBreaker !== undefined;
  }

  /** @internal — usado por `getCatastroGeocoderV2(deps?)` para inyección perezosa. */
  injectOvcService(svc: CatastroService): void {
    if (this.ovcService === undefined) this.ovcService = svc;
  }

  /** @internal — usado por `getCatastroGeocoderV2(deps?)` para inyección perezosa. */
  injectCircuitBreaker(cb: CircuitBreakerManager): void {
    if (this.circuitBreaker === undefined) this.circuitBreaker = cb;
  }

  // ---------------------------------------------------------------------------
  // GEOCODE → GeocodingResult estándar
  // ---------------------------------------------------------------------------

  /**
   * Geocodifica una infraestructura usando Catastro R2.
   *
   * @returns GeocodingResult estándar o null si no encuentra
   */
  async geocode(options: CatastroR2SearchOptions): Promise<GeocodingResult | null> {
    const results = await this.search(options);
    if (results.length === 0) return null;
    return this.toGeocodingResult(results[0], options);
  }

  /**
   * R2-FIRST: geocodifica primero con R2 offline, escala a OVC online sólo si:
   *   1. R2 retorna null o `confidence < 50` (cláusula defensiva — hoy R2 ≥ 90).
   *   2. `options.refcat` está presente (caso defendible: RC explícita
   *      post-snapshot — referencia creada después del 12-mar-2026 que aún no
   *      figura en R2).
   *   3. Circuit-breaker `CATASTRO_OVC` cerrado (`isAvailable === true`).
   *   4. `ovcService` y `circuitBreaker` fueron inyectados en construcción
   *      (vía `getCatastroGeocoderV2({ovcService, circuitBreaker})`).
   *
   * **DOP1-B** (F1-R2-FIRST chat 1/2 V2, 8-may-2026): OVC se acepta sólo con
   * `confidence >= 70` (umbral histórico del bloque [M4] RC en nombre del
   * orchestrator). R2 acepta `>= 50` por consistencia con el rescate scoped
   * PROP-C, pero `searchByRefcat` siempre devuelve ≥ 90 en producción → la
   * cláusula `< 50` es cinturón defensivo ante cambios futuros de scoring.
   *
   * **DOP2**: el circuit-breaker `CATASTRO_OVC` es estructuralmente inerte
   * porque `CatastroService.geocode` engulle excepciones (catch interno →
   * retorna `null` indistintamente para no-match y para error de red).
   * Mientras eso no cambie, el breaker nunca abrirá por error de red — sólo
   * abre si `recordFailure` se invoca explícitamente (caso defensivo del
   * `try/catch` de abajo, que en la práctica nunca dispara). Tracked en
   * `OBS-FT-CATASTRO-OVC-CIRCUIT-BREAKER-INERT` (sev. B).
   *
   * @param options - Opciones de búsqueda. Si falta `codINE`, se usa `codMun`.
   *                  Para escalar a OVC se leen `municipality` y `province`.
   * @param codMun - Código INE5 del municipio (fallback si `options.codINE`
   *                 viene vacío; permite invocar el método sin construir antes
   *                 todo el `CatastroR2SearchOptions`).
   * @returns `GeocodingResult` con `source='catastro_r2_v2'` (R2 hit) o
   *          `source='catastro_api'` (OVC fallback hit), o `null`.
   */
  async geocodeWithOVCFallback(
    options: CatastroR2SearchOptions,
    codMun: string,
  ): Promise<GeocodingResult | null> {
    // Asegurar codINE: prioridad al pasado en options, fallback a codMun
    const opts: CatastroR2SearchOptions = {
      ...options,
      codINE: options.codINE && options.codINE.length > 0 ? options.codINE : codMun,
    };

    // ── Intento 1: R2 offline ───────────────────────────────────────────────
    const r2Result = await this.geocode(opts);
    if (r2Result && r2Result.confidence >= 50) {
      console.log(
        `[R2-FIRST] ✅ R2 hit: refcat=${opts.refcat ?? '-'} codMun=${codMun} ` +
        `conf=${r2Result.confidence}`,
      );
      return r2Result;
    }

    // ── Intento 2: OVC online (sólo con RC explícita) ───────────────────────
    if (!opts.refcat) {
      console.log(`[R2-FIRST] ⏭️  OVC SKIP (sin refcat): codMun=${codMun}`);
      return r2Result;
    }
    if (!this.ovcService || !this.circuitBreaker) {
      console.log(`[R2-FIRST] ⏭️  OVC SKIP (deps no inyectadas): codMun=${codMun}`);
      return r2Result;
    }
    if (!this.circuitBreaker.isAvailable('CATASTRO_OVC')) {
      console.log(`[R2-FIRST] ⏭️  OVC SKIP (circuit open): refcat=${opts.refcat}`);
      return r2Result;
    }

    try {
      const ovcRes = await this.ovcService.geocode({
        cadastralRef: opts.refcat,
        municipality: opts.municipality ?? '',
        province: opts.province ?? '',
      });
      // DOP1-B: umbral OVC ≥ 70 (no 50). Conserva listón histórico del bloque [M4].
      if (ovcRes && ovcRes.confidence >= 70) {
        this.circuitBreaker.recordSuccess('CATASTRO_OVC');
        console.log(
          `[R2-FIRST] ✅ OVC FALLBACK HIT: refcat=${opts.refcat} ` +
          `conf=${ovcRes.confidence}`,
        );
        return { ...ovcRes, source: 'catastro_api' } as GeocodingResult;
      }
      // OVC respondió pero conf<70 o null → cuenta como success (CatastroService
      // no distingue null-por-no-match vs null-por-error; ver DOP2). Pasamos
      // por `recordSuccess` para no abrir el breaker indebidamente.
      this.circuitBreaker.recordSuccess('CATASTRO_OVC');
      console.log(
        `[R2-FIRST] 🔍 OVC FALLBACK MISS: refcat=${opts.refcat} ` +
        `conf=${ovcRes?.confidence ?? 'null'}`,
      );
      return null;
    } catch (err) {
      // DOP2: catch defensivo — `CatastroService.geocode` actualmente no lanza,
      // pero si en el futuro lo hace, capturamos como failure del breaker.
      this.circuitBreaker.recordFailure('CATASTRO_OVC');
      console.warn(
        `[R2-FIRST] ❌ OVC FALLBACK ERROR (non-blocking): refcat=${opts.refcat}`,
        err,
      );
      return r2Result;
    }
  }

  /**
   * Geocodifica retornando múltiples resultados internos.
   */
  async geocodeMultiple(
    options: CatastroR2SearchOptions,
  ): Promise<GeocodingResult[]> {
    const results = await this.search(options);
    return results.map(r => this.toGeocodingResult(r, options));
  }

  // ---------------------------------------------------------------------------
  // ORQUESTACIÓN DE BÚSQUEDA
  // ---------------------------------------------------------------------------

  /**
   * Busca parcelas combinando las 5 estrategias del DataService.
   *
   * El orden de prioridad es:
   * 1. refcat → 2. polígono/parcela → 3. dirección → 4. fuzzy → 5. proximidad
   */
  async search(options: CatastroR2SearchOptions): Promise<CatastroR2Result[]> {
    const {
      codINE,
      refcat,
      via,
      numero,
      name,
      polygon: explicitPoly,
      parcel: explicitParcel,
      usos,
      minConfidence = 50,
      maxResults = 5,
    } = options;

    // Asegurar carga del municipio (el DataService gestiona caché LRU)
    await this.service.loadMunicipality(codINE);

    let candidates: CatastroR2Result[] = [];

    // ── 1. Referencia catastral ─────────────────────────────────────────────
    if (refcat) {
      const hit = await this.service.searchByRefcat(codINE, refcat);
      if (hit) candidates.push(hit);
    }

    // ── 2. Polígono/Parcela (explícito o auto-detectado) ────────────────────
    if (candidates.length === 0) {
      let poly = explicitPoly;
      let parcel = explicitParcel;

      // Auto-detección en campos name / via
      if (!poly || !parcel) {
        for (const text of [name, via].filter(Boolean) as string[]) {
          const pattern = this.service.detectCadastralPattern(text);
          if (pattern) {
            if (pattern.fullRef && !refcat) {
              const byRef = await this.service.searchByRefcat(codINE, pattern.fullRef);
              if (byRef) { candidates.push(byRef); break; }
            }
            if (pattern.polygon && pattern.parcel) {
              poly = pattern.polygon;
              parcel = pattern.parcel;
              break;
            }
          }
        }
      }

      if (poly && parcel && candidates.length === 0) {
        const hits = await this.service.searchByPolygonParcel(codINE, poly, parcel);
        candidates.push(...hits);
      }
    }

    // ── 3. Dirección (vía + número) ─────────────────────────────────────────
    if (via && candidates.length === 0) {
      const hits = await this.service.searchByAddress(codINE, via, numero);
      candidates.push(...hits);
    }

    // ── 4. Fuzzy matching por nombre ────────────────────────────────────────
    if (name && candidates.length === 0) {
      const hits = await this.service.searchByName(codINE, name, usos);
      candidates.push(...hits);
    }

    // ── Filtro por usos (aplica a todas las estrategias) ────────────────────
    if (usos?.length) {
      candidates = candidates.filter(c => usos.includes(c.parcel.uso_cod));
    }

    // ── Filtro confianza mínima + límite ────────────────────────────────────
    return candidates
      .filter(c => c.confidence >= minConfidence)
      .sort((a, b) => b.confidence - a.confidence)
      .slice(0, maxResults);
  }

  // ---------------------------------------------------------------------------
  // DETECCIÓN DE PATRONES CATASTRALES
  // ---------------------------------------------------------------------------

  /**
   * Detecta patrones catastrales en texto libre.
   * Delegado directo al DataService.
   */
  detectCadastralPattern(text: string): CadastralPatternMatch | null {
    return this.service.detectCadastralPattern(text);
  }

  // ---------------------------------------------------------------------------
  // CONVERSIÓN A GeocodingResult ESTÁNDAR
  // ---------------------------------------------------------------------------

  private toGeocodingResult(
    result: CatastroR2Result,
    options: CatastroR2SearchOptions,
  ): GeocodingResult {
    const p = result.parcel;
    const address = `${p.via} ${p.nombre_via} ${p.numero}`.trim();

    return {
      x: result.x,
      y: result.y,
      confidence: result.confidence,
      source: 'catastro_r2_v2',
      sourceLayer: 'catastro-inmuebles',
      originalQuery: options.name || options.via || options.refcat,
      matchedName: p.destino || address,
      matchType: this.inferMatchType(options),
      fuzzyScore: result.fuzzyScore,
      address,
      municipality: p.municipio,
      muniCode: p.cod_ine,
      metadata: {
        refcat: p.refcat,
        uso: p.uso,
        usoCod: p.uso_cod,
        destino: p.destino,
        precision: result.precision,
      },
      // PAR1 · HX-CAPTURA-MAXIMAL: la parcela entera, no su denominador común.
      // Las 5 claves de `metadata` de arriba se mantienen intactas: hay
      // consumidores vivos que las leen (p. ej. `wfsFallbackValidator` lee
      // `usoCod`) y esta rebanada añade, no reordena.
      catastroParcel: p,
    };
  }

  private inferMatchType(options: CatastroR2SearchOptions): string {
    if (options.refcat) return 'refcat';
    if (options.polygon || options.parcel) return 'polygon_parcel';
    if (options.via) return 'address';
    return 'fuzzy';
  }

  // ---------------------------------------------------------------------------
  // UTILIDADES
  // ---------------------------------------------------------------------------

  /** Códigos de uso Catastro para una tipología PTEL */
  getUsosForTipologia(tipologia: string): string[] {
    const upper = tipologia.toUpperCase();
    if ((TIPOLOGIAS_VALUES as readonly string[]).includes(upper)) {
      return tipologiaACodigoCatastro(upper as TipologiaPTEL);
    }
    return [];
  }

  /** Precarga municipio (útil antes de geocodificación masiva) */
  async preloadMunicipality(codINE: string): Promise<void> {
    await this.service.loadMunicipality(codINE);
  }

  /** Verifica si un municipio está en caché */
  hasMunicipio(codINE: string): boolean {
    return this.service.getStats().loadedCodINEs.includes(codINE);
  }

  /** Estadísticas del servicio */
  getStats(): CatastroServiceStats {
    return this.service.getStats();
  }
}

// =============================================================================
// HELPER PARA ORCHESTRATOR
// =============================================================================

/**
 * Función helper compatible con GeocodingOrchestrator.
 *
 * Interfaz simplificada que acepta nombre + codINE y devuelve coordenadas.
 */
export async function geocodeWithCatastroV2(
  name: string,
  codINE: string,
  options?: { via?: string; numero?: string; refcat?: string; usos?: string[] },
): Promise<{
  x: number;
  y: number;
  confidence: number;
  source: string;
  matchedName: string;
  municipality?: string;
  muniCode?: string;
} | null> {
  const geocoder = getCatastroGeocoderV2();

  const result = await geocoder.geocode({
    codINE,
    name,
    via: options?.via,
    numero: options?.numero,
    refcat: options?.refcat,
    usos: options?.usos,
  });

  if (!result) return null;

  return {
    x: result.x,
    y: result.y,
    confidence: result.confidence,
    source: 'catastro_r2_v2',
    matchedName: result.matchedName,
    municipality: result.municipality,
    muniCode: result.muniCode,
  };
}

export default CatastroGeocoderV2;
